"use client";

import { MouseEvent } from "react";
import Text from "apps/website/components/base/Text/Text";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { IStoryBlokComponent } from "apps/website/components/page/api.types";

export interface IArticleContentsHeading {
  text: string;
  id: string;
}

export interface IStoryBlokArticleContents {
  title?: string;
}

export interface IStoryBlokArticleContentsProps
  extends IStoryBlokComponent<IStoryBlokArticleContents> {
  headings?: IArticleContentsHeading[];
}

export const StoryBlokArticleContents = ({
  blok,
  headings = [],
}: IStoryBlokArticleContentsProps) => {
  if (!headings.length) {
    return null;
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    // Always prevent the native anchor jump first. If we bail out before this
    // (e.g. the target node is momentarily detached while the Storyblok bridge
    // re-renders the article), the browser follows the `#id` href and scrolls
    // to the top of the page instead of the section.
    event.preventDefault();
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    // Set the hash before scrolling so the global scroll-reset in RootEffects
    // sees the hash and leaves our in-page navigation alone.
    window.history.replaceState(null, "", `#${id}`);
    target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        aria-label={blok.title || "In this article"}
        className="flex flex-col gap-4 bg-[#DEDCF3] p-4"
      >
        <Text tag="p" display="title" size="default" className="text-black leading-looser">
          { blok.title || "In this article" }
        </Text>
        <ul className="flex flex-col gap-2 pl-2">
          { headings.map((heading) => (
            <li key={heading.id}>
              <a href={`#${heading.id}`} onClick={(event) => handleClick(event, heading.id)}>
                <Text
                  tag="span"
                  display="button"
                  size="sm"
                  className="text-[#473D80] tracking-[-0.36px] leading-none hover:underline"
                >
                  { heading.text }
                </Text>
              </a>
            </li>
          )) }
        </ul>
      </nav>
      <Spacer size="xl" />
    </>
  );
};
